import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, differenceInDays, eachMonthOfInterval } from 'date-fns';
import { seasonService, CreateSeasonRequest, UpdateSeasonRequest, PhaseRequest } from '../services/endpoints/seasonService';
import { showService } from '../services/endpoints/showService';
import { pedService } from '../services/endpoints/pedService';
import { ContestSeason, SeasonStatus, Show, ShowPriority, Phase, PhaseType, PEDCycle } from '../types';
import { parseLocalDate } from '../utils/dateUtils';
import { useCoachView } from '../contexts/CoachViewContext';

// ── helpers ──────────────────────────────────────────────────────────────────

const statusBadge: Record<string, string> = {
  Planned:   'bg-blue-900/40 text-blue-300 border-blue-600/40',
  Active:    'bg-green-900/40 text-green-300 border-green-600/40',
  Completed: 'bg-gray-700/60 text-gray-400 border-gray-600/40',
  Archived:  'bg-dark-700 text-gray-600 border-gray-700',
};

const priorityBadge: Record<string, string> = {
  Primary:   'bg-yellow-900/40 text-gold-400 border-yellow-600/40',
  Secondary: 'bg-gray-700/40 text-gray-400 border-gray-600/40',
  Backup:    'bg-orange-900/40 text-orange-400 border-orange-600/40',
  TuneUp:    'bg-blue-900/40 text-blue-300 border-blue-600/40',
  Added:     'bg-purple-900/40 text-purple-300 border-purple-600/40',
};

const priorityLabel: Record<string, string> = {
  Primary: 'Primary', Secondary: 'Secondary', Backup: 'Backup', TuneUp: 'Tune-Up', Added: 'Added',
};

function formatDate(iso: string) {
  return parseLocalDate(iso.split('T')[0]).toLocaleDateString();
}

function seasonDateRange(season: ContestSeason) {
  const start = formatDate(season.startDate);
  const end = season.endDate ? formatDate(season.endDate) : 'ongoing';
  return `${start} – ${end}`;
}

// ── phase helpers ─────────────────────────────────────────────────────────────

const phaseTypeBadge: Record<string, string> = {
  Cruise:       'bg-blue-900/40 text-blue-300 border-blue-600/40',
  MiniCut:      'bg-rose-900/40 text-rose-300 border-rose-600/40',
  Hold:         'bg-slate-700/40 text-slate-300 border-slate-500/40',
  Bridge:       'bg-violet-900/40 text-violet-300 border-violet-600/40',
  Rebound:      'bg-teal-900/40 text-teal-300 border-teal-600/40',
  GrowthBlast:  'bg-green-900/40 text-green-300 border-green-600/40',
  ContestPrep:  'bg-orange-900/40 text-orange-300 border-orange-600/40',
  Show:         'bg-gold-500/10 text-gold-400 border-yellow-500/40',
};

const phaseTypeLabel: Record<string, string> = {
  Cruise:       'Cruise',
  MiniCut:      'Mini Cut',
  Hold:         'Hold',
  Bridge:       'Bridge',
  Rebound:      'Rebound',
  GrowthBlast:  'Growth Blast',
  ContestPrep:  'Contest Prep',
  Show:         'Show / Peak Week',
};

const PHASE_COLORS: Record<string, { bg: string; text: string }> = {
  Cruise:       { bg: '#1e3a5f', text: '#93c5fd' },
  MiniCut:      { bg: '#3b1a1a', text: '#fca5a5' },
  Hold:         { bg: '#2b3040', text: '#cbd5e1' },
  Bridge:       { bg: '#2e1e47', text: '#c4b5fd' },
  Rebound:      { bg: '#0e3538', text: '#5eead4' },
  GrowthBlast:  { bg: '#14352a', text: '#6ee7b7' },
  ContestPrep:  { bg: '#3b1500', text: '#fed7aa' },
  Show:         { bg: '#3d2e00', text: '#fbbf24' },
};

// 15 icons for non-primary shows — deterministically assigned by showId hash
const SHOW_ICONS = ['⭐', '🔥', '⚡', '💎', '🎯', '🌟', '🏅', '👑', '💪', '🎖️', '🔱', '🌙', '☀️', '🛡️', '⚔️'];

function showIconIndex(showId: string): number {
  let h = 0;
  for (let i = 0; i < showId.length; i++) {
    h = (h * 31 + showId.charCodeAt(i)) >>> 0;
  }
  return h % SHOW_ICONS.length;
}

function getShowIcon(showId: string, isPrimary: boolean): string {
  return isPrimary ? '🏆' : SHOW_ICONS[showIconIndex(showId)];
}

function shiftDateStr(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr.split('T')[0]);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function endDateFromDuration(startDate: string, weeks: number): string {
  return shiftDateStr(startDate, weeks * 7 - 1);
}

// ── blank season form ─────────────────────────────────────────────────────────

const blankSeasonForm = () => ({ name: '', startDate: '', endDate: '', status: 'Active' });

// ── ShowRow ───────────────────────────────────────────────────────────────────

interface ShowRowProps {
  show: Show;
  onEdit?: () => void;
  onDelete?: () => void;
  confirmDelete?: boolean;
  onConfirmDelete?: () => void;
  onCancelDelete?: () => void;
}

const ShowRow: React.FC<ShowRowProps> = ({
  show, onEdit, onDelete, confirmDelete, onConfirmDelete, onCancelDelete,
}) => {
  const priority = show.showPriority as string | undefined;
  const icon = getShowIcon(show.showId, priority === 'Primary');
  return (
    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-dark-900/60 border border-dark-600">
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex-shrink-0 text-base leading-none" title="Timeline icon">{icon}</span>
        {priority && (
          <span className={`flex-shrink-0 px-2 py-0.5 text-xs rounded border ${priorityBadge[priority] ?? priorityBadge.Added}`}>
            {priorityLabel[priority] ?? priority}
          </span>
        )}
        <span className="font-semibold text-white truncate">{show.name}</span>
      </div>
      <div className="flex items-center gap-3 text-sm text-gray-400 flex-shrink-0 ml-4">
        <span className="hidden sm:inline">{show.category}{show.division ? ` · ${show.division}` : ''}</span>
        <span>{formatDate(show.date)}</span>
        {confirmDelete ? (
          <div className="flex gap-1">
            <button onClick={onConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-semibold">
              Confirm
            </button>
            <button onClick={onCancelDelete}
              className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs">
              Cancel
            </button>
          </div>
        ) : (
          <>
            {onEdit && (
              <button
                onClick={onEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button onClick={onDelete}
                className="bg-gray-700 hover:bg-red-800 text-gray-400 hover:text-red-200 px-2 py-1 rounded text-xs">
                Delete
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── ShowEditPanel ─────────────────────────────────────────────────────────────

interface ShowEditPanelProps {
  show: Show;
  seasons: ContestSeason[];
  onSave: (data: Partial<Show>) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const ShowEditPanel: React.FC<ShowEditPanelProps> = ({ show, seasons, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState({
    name:         show.name,
    date:         show.date.split('T')[0],
    location:     show.location,
    category:     show.category,
    division:     show.division ?? '',
    targetWeight: show.targetWeight?.toString() ?? '',
    showPriority: (show.showPriority as string) ?? 'Primary',
    seasonId:     show.seasonId ?? '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name:         form.name,
      date:         form.date,
      location:     form.location,
      category:     form.category,
      division:     form.division || undefined,
      targetWeight: form.targetWeight ? parseFloat(form.targetWeight) : undefined,
      showPriority: form.showPriority as ShowPriority,
      seasonId:     form.seasonId || undefined,
      // preserve existing phases so the backend update doesn't wipe them
      phases:       show.phases,
    });
  };

  const inputCls = 'w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500';
  const labelCls = 'block text-xs font-medium text-gray-400 mb-1';

  return (
    <div className="rounded-lg border border-blue-600/30 bg-dark-700/60 p-4">
      <p className="text-sm font-semibold text-gold-400 mb-3">Editing: {show.name}</p>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded text-sm mb-3">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Show Name *</label>
            <input type="text" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Show Date *</label>
            <input type="date" required value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Location *</label>
            <input type="text" required value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}
              className={inputCls} placeholder="Dallas, TX" />
          </div>

          <div>
            <label className={labelCls}>Category *</label>
            <select required value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className={inputCls}>
              <option value="">Select Category</option>
              <option value="Classic Physique">Classic Physique</option>
              <option value="Men's Physique">Men's Physique</option>
              <option value="Bodybuilding">Bodybuilding</option>
              <option value="Women's Physique">Women's Physique</option>
              <option value="Bikini">Bikini</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Division</label>
            <input type="text" value={form.division}
              onChange={e => setForm({ ...form, division: e.target.value })}
              className={inputCls} placeholder="Masters 50+" />
          </div>

          <div>
            <label className={labelCls}>Target Weight (lbs)</label>
            <input type="number" step="0.1" value={form.targetWeight}
              onChange={e => setForm({ ...form, targetWeight: e.target.value })}
              className={inputCls} placeholder="182.0" />
          </div>

          <div>
            <label className={labelCls}>Show Priority</label>
            <select value={form.showPriority}
              onChange={e => setForm({ ...form, showPriority: e.target.value })}
              className={inputCls}>
              <option value="Primary">Primary</option>
              <option value="Secondary">Secondary</option>
              <option value="Backup">Backup</option>
              <option value="TuneUp">Tune-Up</option>
              <option value="Added">Added</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>Season</label>
            <select value={form.seasonId}
              onChange={e => setForm({ ...form, seasonId: e.target.value })}
              className={inputCls}>
              <option value="">— No season —</option>
              {seasons.map(s => (
                <option key={s.seasonId} value={s.seasonId}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-gold-500 hover:bg-gold-600 disabled:opacity-50 text-black font-bold py-1.5 px-5 rounded text-sm transition-colors">
            {saving ? 'Saving…' : 'Save Show'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-1.5 px-5 rounded text-sm transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── SeasonTimeline ────────────────────────────────────────────────────────────

interface SeasonTimelineProps {
  season: ContestSeason;
  shows: Show[];
  phases: Phase[];
}

const SeasonTimeline: React.FC<SeasonTimelineProps> = ({ season, shows, phases }) => {
  const { viewAs } = useCoachView();
  const { data: allCycles = [] } = useQuery({
    queryKey: ['ped-cycles', viewAs],
    queryFn: () => pedService.getCycles(viewAs ?? undefined),
  });

  const cycles: PEDCycle[] = allCycles.filter(c => c.seasonId === season.seasonId);

  const tStart = parseISO(season.startDate.split('T')[0]);

  let tEnd: Date;
  if (season.endDate) {
    tEnd = parseISO(season.endDate.split('T')[0]);
  } else {
    const candidates: number[] = [
      Date.now() + 60 * 24 * 60 * 60 * 1000,
    ];
    phases.forEach(p => candidates.push(parseISO(p.endDate.split('T')[0]).getTime()));
    shows.forEach(s => candidates.push(parseISO(s.date.split('T')[0]).getTime()));
    cycles.forEach(c => {
      if (c.endDate) {
        candidates.push(parseISO(c.endDate.split('T')[0]).getTime());
      } else {
        candidates.push(parseISO(c.startDate.split('T')[0]).getTime() + 28 * 24 * 60 * 60 * 1000);
      }
    });
    tEnd = new Date(Math.max(...candidates));
  }

  if (tEnd <= tStart) {
    tEnd = new Date(tStart.getTime() + 90 * 24 * 60 * 60 * 1000);
  }

  const totalDays = differenceInDays(tEnd, tStart) || 1;
  const toPct = (d: Date) => Math.max(0, Math.min(100, (differenceInDays(d, tStart) / totalDays) * 100));
  const toWidthPct = (s: Date, e: Date) => Math.max(0.5, toPct(e) - toPct(s));

  const today = new Date();
  const todayPct = toPct(today);
  const showToday = todayPct >= 0 && todayPct <= 100;

  const months = eachMonthOfInterval({ start: tStart, end: tEnd });

  if (phases.length === 0 && cycles.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-gray-700 bg-dark-900/40 p-4 text-center">
        <p className="text-xs text-gray-600">
          Add phases and assign PED cycles to this season to see the timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">Timeline</h4>
      <div className="rounded-lg border border-dark-600 bg-dark-900/40 p-4 overflow-x-auto">
        <div style={{ minWidth: '480px' }}>

          {/* Month axis */}
          <div className="relative h-5 mb-1 select-none">
            {months.map((m, i) => {
              const pct = toPct(m);
              return (
                <span
                  key={i}
                  className="absolute text-[10px] text-gray-600 whitespace-nowrap"
                  style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
                >
                  {format(m, 'MMM')}
                </span>
              );
            })}
          </div>

          {/* Phase bands */}
          {phases.length > 0 && (
            <div className="relative h-9 rounded overflow-hidden mb-2 bg-dark-800">
              {phases.map(p => {
                const ps = parseISO(p.startDate.split('T')[0]);
                const pe = parseISO(p.endDate.split('T')[0]);
                const left = toPct(ps);
                const width = toWidthPct(ps, pe);
                const colors = PHASE_COLORS[p.phaseType] ?? { bg: '#374151', text: '#9ca3af' };
                return (
                  <div
                    key={p.phaseId}
                    className="absolute inset-y-0 flex items-center px-2 overflow-hidden"
                    style={{ left: `${left}%`, width: `${width}%`, backgroundColor: colors.bg }}
                    title={`${p.name} (${format(ps, 'MMM d')} – ${format(pe, 'MMM d')})`}
                  >
                    <span className="text-[10px] font-medium truncate" style={{ color: colors.text }}>
                      {p.name}
                    </span>
                  </div>
                );
              })}
              {showToday && (
                <div
                  className="absolute inset-y-0 w-0.5 bg-white/60 pointer-events-none"
                  style={{ left: `${todayPct}%` }}
                />
              )}
            </div>
          )}

          {/* Show markers — icon on top, drop line below */}
          {shows.length > 0 && (
            <div className="relative h-10 mb-2">
              {shows.map(s => {
                const sd = parseISO(s.date.split('T')[0]);
                const pct = toPct(sd);
                const isPrimary = s.showPriority === ShowPriority.Primary;
                const icon = getShowIcon(s.showId, isPrimary);
                return (
                  <div
                    key={s.showId}
                    className="absolute flex flex-col items-center"
                    style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
                    title={`${s.name} – ${format(sd, 'MMM d, yyyy')}`}
                  >
                    <span className="text-sm leading-none">{icon}</span>
                    <div className={`w-0.5 h-5 mt-0.5 ${isPrimary ? 'bg-yellow-400' : 'bg-gray-500'}`} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Cycle rows */}
          {cycles.map(cycle => {
            const cs = parseISO(cycle.startDate.split('T')[0]);
            const ce = cycle.endDate
              ? parseISO(cycle.endDate.split('T')[0])
              : new Date(cs.getTime() + 28 * 24 * 60 * 60 * 1000);
            const left = toPct(cs);
            const width = toWidthPct(cs, ce);
            return (
              <div key={cycle.cycleId} className="relative h-7 mb-1 last:mb-0">
                <div
                  className="absolute inset-y-1 rounded flex items-center overflow-hidden"
                  style={{ left: `${left}%`, width: `${width}%`, backgroundColor: '#78350f' }}
                  title={`${cycle.name} (${format(cs, 'MMM d')} – ${format(ce, 'MMM d')})`}
                >
                  <span className="text-[10px] text-amber-300 px-2 truncate">{cycle.name}</span>
                </div>
                {showToday && (
                  <div
                    className="absolute inset-y-0 w-0.5 bg-white/30 pointer-events-none"
                    style={{ left: `${todayPct}%` }}
                  />
                )}
              </div>
            );
          })}

          {/* Bottom date labels */}
          <div className="flex justify-between mt-2 text-[10px] text-gray-600">
            <span>{format(tStart, 'MMM d, yyyy')}</span>
            <span>{format(tEnd, 'MMM d, yyyy')}</span>
          </div>

        </div>
      </div>
    </div>
  );
};

// ── PhaseRow ──────────────────────────────────────────────────────────────────

interface PhaseRowProps {
  phase: Phase;
  onEdit?: () => void;
  onDelete?: () => void;
  confirmDelete: boolean;
  onConfirmDelete?: () => void;
  onCancelDelete?: () => void;
}

const PhaseRow: React.FC<PhaseRowProps> = ({
  phase, onEdit, onDelete, confirmDelete, onConfirmDelete, onCancelDelete,
}) => {
  const badge = phaseTypeBadge[phase.phaseType] ?? phaseTypeBadge.Cruise;
  const label = phaseTypeLabel[phase.phaseType] ?? phase.phaseType;
  return (
    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-dark-900/60 border border-dark-600">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`flex-shrink-0 px-2 py-0.5 text-xs rounded border ${badge}`}>{label}</span>
        <span className="font-semibold text-white truncate">{phase.name}</span>
      </div>
      <div className="flex items-center gap-3 text-sm text-gray-400 flex-shrink-0 ml-4">
        <span className="hidden sm:inline">{formatDate(phase.startDate)} – {formatDate(phase.endDate)}</span>
        <span className="text-xs text-gray-500">{phase.durationWeeks}w</span>
        {confirmDelete ? (
          <div className="flex gap-1">
            <button onClick={onConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-semibold">
              Confirm
            </button>
            <button onClick={onCancelDelete}
              className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs">
              Cancel
            </button>
          </div>
        ) : (
          <>
            {onEdit && (
              <button onClick={onEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium">
                Edit
              </button>
            )}
            {onDelete && (
              <button onClick={onDelete}
                className="bg-gray-700 hover:bg-red-800 text-gray-400 hover:text-red-200 px-2 py-1 rounded text-xs">
                Delete
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── PhaseEditPanel ────────────────────────────────────────────────────────────

interface PhaseEditPanelProps {
  phase: Phase;
  onSave: (data: PhaseRequest) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const PhaseEditPanel: React.FC<PhaseEditPanelProps> = ({ phase, onSave, onCancel, saving, error }) => {
  const startStr = phase.startDate.split('T')[0];
  const [form, setForm] = useState({
    name:          phase.name,
    phaseType:     phase.phaseType as string,
    startDate:     startStr,
    durationWeeks: phase.durationWeeks.toString(),
    endDate:       endDateFromDuration(startStr, phase.durationWeeks),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name:      form.name,
      phaseType: form.phaseType,
      startDate: form.startDate,
      endDate:   form.endDate,
    });
  };

  const inputCls = 'w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500';
  const labelCls = 'block text-xs font-medium text-gray-400 mb-1';

  return (
    <div className="rounded-lg border border-blue-600/30 bg-dark-700/60 p-4">
      <p className="text-sm font-semibold text-gold-400 mb-3">Editing: {phase.name}</p>
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded text-sm mb-3">{error}</div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Phase Name *</label>
            <input type="text" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phase Type *</label>
            <select required value={form.phaseType}
              onChange={e => setForm({ ...form, phaseType: e.target.value })}
              className={inputCls}>
              {Object.entries(phaseTypeLabel).map(([val, lbl]) => (
                <option key={val} value={val}>{lbl}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Start Date *</label>
            <input type="date" required value={form.startDate}
              onChange={e => {
                const start = e.target.value;
                setForm(f => ({
                  ...f,
                  startDate: start,
                  endDate: start ? endDateFromDuration(start, parseInt(f.durationWeeks, 10) || 1) : f.endDate,
                }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Duration (weeks)</label>
            <input type="number" min="1" value={form.durationWeeks}
              onChange={e => {
                const weeks = e.target.value;
                setForm(f => ({
                  ...f,
                  durationWeeks: weeks,
                  endDate: f.startDate && weeks ? endDateFromDuration(f.startDate, parseInt(weeks, 10) || 1) : f.endDate,
                }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date *</label>
            <input type="date" required value={form.endDate}
              onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
              className={inputCls} />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-gold-500 hover:bg-gold-600 disabled:opacity-50 text-black font-bold py-1.5 px-5 rounded text-sm transition-colors">
            {saving ? 'Saving…' : 'Save Phase'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-1.5 px-5 rounded text-sm transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── PhaseAddForm ──────────────────────────────────────────────────────────────

interface PhaseAddFormProps {
  lastPhaseEndDate?: string;
  onSave: (data: PhaseRequest) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const PhaseAddForm: React.FC<PhaseAddFormProps> = ({ lastPhaseEndDate, onSave, onCancel, saving, error }) => {
  const defaultStart = lastPhaseEndDate
    ? shiftDateStr(lastPhaseEndDate, 1)
    : new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    name:          '',
    phaseType:     PhaseType.GrowthBlast as string,
    startDate:     defaultStart,
    durationWeeks: '8',
    endDate:       endDateFromDuration(defaultStart, 8),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name:      form.name,
      phaseType: form.phaseType,
      startDate: form.startDate,
      endDate:   form.endDate,
    });
  };

  const inputCls = 'w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500';
  const labelCls = 'block text-xs font-medium text-gray-400 mb-1';

  return (
    <div className="rounded-lg border border-green-600/30 bg-dark-700/60 p-4 mt-2">
      <p className="text-sm font-semibold text-green-400 mb-3">Add Phase</p>
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded text-sm mb-3">{error}</div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Phase Name *</label>
            <input type="text" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className={inputCls} placeholder="e.g., Contest Prep" />
          </div>
          <div>
            <label className={labelCls}>Phase Type *</label>
            <select required value={form.phaseType}
              onChange={e => setForm({ ...form, phaseType: e.target.value })}
              className={inputCls}>
              {Object.entries(phaseTypeLabel).map(([val, lbl]) => (
                <option key={val} value={val}>{lbl}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Start Date *</label>
            <input type="date" required value={form.startDate}
              onChange={e => {
                const start = e.target.value;
                setForm(f => ({
                  ...f,
                  startDate: start,
                  endDate: start ? endDateFromDuration(start, parseInt(f.durationWeeks, 10) || 1) : f.endDate,
                }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Duration (weeks)</label>
            <input type="number" min="1" value={form.durationWeeks}
              onChange={e => {
                const weeks = e.target.value;
                setForm(f => ({
                  ...f,
                  durationWeeks: weeks,
                  endDate: f.startDate && weeks ? endDateFromDuration(f.startDate, parseInt(weeks, 10) || 1) : f.endDate,
                }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date *</label>
            <input type="date" required value={form.endDate}
              onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
              className={inputCls} />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-1.5 px-5 rounded text-sm transition-colors">
            {saving ? 'Adding…' : 'Add Phase'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-1.5 px-5 rounded text-sm transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── ShowAddForm ───────────────────────────────────────────────────────────────

interface ShowAddFormProps {
  onSave: (data: Partial<Show>) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const ShowAddForm: React.FC<ShowAddFormProps> = ({ onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState({
    name:         '',
    date:         '',
    location:     '',
    category:     '',
    division:     '',
    targetWeight: '',
    showPriority: 'Primary' as string,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name:         form.name,
      date:         form.date,
      location:     form.location,
      category:     form.category,
      division:     form.division || undefined,
      targetWeight: form.targetWeight ? parseFloat(form.targetWeight) : undefined,
      showPriority: form.showPriority as ShowPriority,
    });
  };

  const inputCls = 'w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500';
  const labelCls = 'block text-xs font-medium text-gray-400 mb-1';

  return (
    <div className="rounded-lg border border-green-600/30 bg-dark-700/60 p-4 mt-2">
      <p className="text-sm font-semibold text-green-400 mb-3">Add Show</p>
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded text-sm mb-3">{error}</div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Show Name *</label>
            <input type="text" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className={inputCls} placeholder="2027 Ronnie Coleman Classic" />
          </div>
          <div>
            <label className={labelCls}>Show Date *</label>
            <input type="date" required value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Location *</label>
            <input type="text" required value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}
              className={inputCls} placeholder="Dallas, TX" />
          </div>
          <div>
            <label className={labelCls}>Category *</label>
            <select required value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className={inputCls}>
              <option value="">Select Category</option>
              <option value="Classic Physique">Classic Physique</option>
              <option value="Men's Physique">Men's Physique</option>
              <option value="Bodybuilding">Bodybuilding</option>
              <option value="Women's Physique">Women's Physique</option>
              <option value="Bikini">Bikini</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Division</label>
            <input type="text" value={form.division}
              onChange={e => setForm({ ...form, division: e.target.value })}
              className={inputCls} placeholder="Masters 50+" />
          </div>
          <div>
            <label className={labelCls}>Target Weight (lbs)</label>
            <input type="number" step="0.1" value={form.targetWeight}
              onChange={e => setForm({ ...form, targetWeight: e.target.value })}
              className={inputCls} placeholder="182.0" />
          </div>
          <div>
            <label className={labelCls}>Show Priority</label>
            <select value={form.showPriority}
              onChange={e => setForm({ ...form, showPriority: e.target.value })}
              className={inputCls}>
              <option value="Primary">Primary</option>
              <option value="Secondary">Secondary</option>
              <option value="Backup">Backup</option>
              <option value="TuneUp">Tune-Up</option>
              <option value="Added">Added</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-1.5 px-5 rounded text-sm transition-colors">
            {saving ? 'Adding…' : 'Add Show'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-1.5 px-5 rounded text-sm transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ── SeasonForm ────────────────────────────────────────────────────────────────

interface SeasonFormProps {
  initial?: ContestSeason;
  onSave: (data: CreateSeasonRequest | UpdateSeasonRequest) => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

const SeasonForm: React.FC<SeasonFormProps> = ({ initial, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState(() =>
    initial
      ? {
          name:      initial.name,
          startDate: initial.startDate.split('T')[0],
          endDate:   initial.endDate ? initial.endDate.split('T')[0] : '',
          status:    initial.status as string,
        }
      : blankSeasonForm()
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name:      form.name,
      startDate: form.startDate,
      endDate:   form.endDate || undefined,
      ...(initial ? { status: form.status as SeasonStatus } : {}),
    });
  };

  const inputCls = 'w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">{error}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-1">Season Name *</label>
          <input type="text" required value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className={inputCls} placeholder="2027 Prep Season" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
          <input type="date" required value={form.startDate}
            onChange={e => setForm({ ...form, startDate: e.target.value })}
            className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            End Date <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <input type="date" value={form.endDate}
            onChange={e => setForm({ ...form, endDate: e.target.value })}
            className={inputCls} />
        </div>
        {initial && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
              className={inputCls}>
              <option value="Planned">Planned</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving}
          className="bg-gold-500 hover:bg-gold-600 disabled:opacity-50 text-black font-bold py-2 px-6 rounded transition-colors">
          {saving ? 'Saving…' : initial ? 'Update Season' : 'Create Season'}
        </button>
        <button type="button" onClick={onCancel}
          className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// ── SeasonCard ────────────────────────────────────────────────────────────────

interface SeasonCardProps {
  season: ContestSeason;
  shows: Show[];
  seasons: ContestSeason[];   // all seasons, for the show reassignment dropdown
  isCoachView: boolean;
  onEditSeason: (s: ContestSeason) => void;
  onArchive: (id: string) => void;
  confirmArchiveId: string | null;
  setConfirmArchiveId: (id: string | null) => void;
  // show editing — managed at page level so only one show is open at a time
  editingShowId: string | null;
  onEditShow: (id: string) => void;
  onSaveShow: (id: string, data: Partial<Show>) => void;
  onCancelEditShow: () => void;
  showSaving: boolean;
  showEditError: string | null;
}

const SeasonCard: React.FC<SeasonCardProps> = ({
  season, shows, seasons, isCoachView,
  onEditSeason, onArchive, confirmArchiveId, setConfirmArchiveId,
  editingShowId, onEditShow, onSaveShow, onCancelEditShow, showSaving, showEditError,
}) => {
  const queryClient = useQueryClient();
  const { viewAs } = useCoachView();
  const [expanded, setExpanded] = useState(season.status === 'Active' || season.status === 'Planned');
  const isArchived = season.status === 'Archived';

  // ── phase state ──
  const [editingPhaseId, setEditingPhaseId]         = useState<string | null>(null);
  const [phaseEditError, setPhaseEditError]         = useState<string | null>(null);
  const [showAddPhaseForm, setShowAddPhaseForm]     = useState(false);
  const [phaseAddError, setPhaseAddError]           = useState<string | null>(null);
  const [confirmDeletePhaseId, setConfirmDeletePhaseId] = useState<string | null>(null);

  // ── phase query ──
  const { data: phases = [] } = useQuery({
    queryKey: ['season-phases', season.seasonId, viewAs],
    queryFn: () => seasonService.getSeasonPhases(season.seasonId, viewAs ?? undefined),
    enabled: expanded,
  });

  // ── phase mutations ──
  const invalidatePhases = () =>
    queryClient.invalidateQueries({ queryKey: ['season-phases', season.seasonId] });

  const createPhaseMutation = useMutation({
    mutationFn: (req: PhaseRequest) => seasonService.createSeasonPhase(season.seasonId, req),
    onSuccess: () => {
      invalidatePhases();
      setShowAddPhaseForm(false);
      setPhaseAddError(null);
    },
    onError: () => setPhaseAddError('Failed to add phase'),
  });

  const updatePhaseMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: PhaseRequest }) =>
      seasonService.updatePhase(id, req),
    onSuccess: () => {
      invalidatePhases();
      setEditingPhaseId(null);
      setPhaseEditError(null);
    },
    onError: () => setPhaseEditError('Failed to update phase'),
  });

  const deletePhaseMutation = useMutation({
    mutationFn: (id: string) => seasonService.deletePhase(id),
    onSuccess: () => {
      invalidatePhases();
      setConfirmDeletePhaseId(null);
      // Also invalidate cycle queries since cycles may have had their phaseId nulled
      queryClient.invalidateQueries({ queryKey: ['ped-cycles'] });
    },
    onError: () => setConfirmDeletePhaseId(null),
  });

  // ── show add/delete state ──
  const [showAddForm, setShowAddForm]   = useState(false);
  const [showAddError, setShowAddError] = useState<string | null>(null);
  const [confirmDeleteShowId, setConfirmDeleteShowId] = useState<string | null>(null);

  const createShowMutation = useMutation({
    mutationFn: (data: Partial<Show>) =>
      showService.createShow({ ...data, seasonId: season.seasonId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shows'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowAddForm(false);
      setShowAddError(null);
    },
    onError: () => setShowAddError('Failed to add show'),
  });

  const deleteShowMutation = useMutation({
    mutationFn: (id: string) => showService.deleteShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shows'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['ped-cycles'] });
      setConfirmDeleteShowId(null);
    },
    onError: () => setConfirmDeleteShowId(null),
  });

  const sortedShows = [...shows].sort((a, b) => {
    if (a.showPriority === ShowPriority.Primary && b.showPriority !== ShowPriority.Primary) return -1;
    if (b.showPriority === ShowPriority.Primary && a.showPriority !== ShowPriority.Primary) return 1;
    return a.date.localeCompare(b.date);
  });

  const lastPhaseEndDate = phases.length > 0
    ? phases[phases.length - 1].endDate
    : undefined;

  return (
    <div className={`bg-dark-800 rounded-lg overflow-hidden border border-dark-600 ${isArchived ? 'opacity-70' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <button
          className="flex items-center gap-3 text-left flex-1 min-w-0"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
        >
          <span className="text-gray-500 text-sm select-none w-4">{expanded ? '▼' : '▶'}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-bold text-gold-500">{season.name}</h3>
              <span className={`px-2 py-0.5 text-xs rounded border ${statusBadge[season.status] ?? statusBadge.Planned}`}>
                {season.status}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-0.5">
              {seasonDateRange(season)} · {shows.length} show{shows.length !== 1 ? 's' : ''} · {phases.length} phase{phases.length !== 1 ? 's' : ''}
            </p>
          </div>
        </button>

        {!isCoachView && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button onClick={() => onEditSeason(season)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
              Edit
            </button>
            {!isArchived && (
              confirmArchiveId === season.seasonId ? (
                <div className="flex gap-1">
                  <button
                    onClick={() => { onArchive(season.seasonId); setConfirmArchiveId(null); }}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-semibold">
                    Confirm Archive
                  </button>
                  <button onClick={() => setConfirmArchiveId(null)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm">
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmArchiveId(season.seasonId)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm">
                  Archive
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-6 pb-5 border-t border-dark-600 space-y-5">

          {/* ── Phases section ── */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500">Phases</h4>
              {!isCoachView && !isArchived && !showAddPhaseForm && (
                <button
                  onClick={() => { setShowAddPhaseForm(true); setEditingPhaseId(null); }}
                  className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded font-medium">
                  + Add Phase
                </button>
              )}
            </div>

            <div className="space-y-2">
              {phases.length === 0 && !showAddPhaseForm && (
                <p className="text-sm text-gray-500 italic">
                  No phases yet. Add phases to define this season's training blocks.
                </p>
              )}
              {phases.map(phase =>
                editingPhaseId === phase.phaseId ? (
                  <PhaseEditPanel
                    key={phase.phaseId}
                    phase={phase}
                    onSave={(req) => updatePhaseMutation.mutate({ id: phase.phaseId, req })}
                    onCancel={() => { setEditingPhaseId(null); setPhaseEditError(null); }}
                    saving={updatePhaseMutation.isPending}
                    error={phaseEditError}
                  />
                ) : (
                  <PhaseRow
                    key={phase.phaseId}
                    phase={phase}
                    onEdit={!isCoachView && !isArchived ? () => {
                      setEditingPhaseId(phase.phaseId);
                      setPhaseEditError(null);
                      setShowAddPhaseForm(false);
                    } : undefined}
                    onDelete={!isCoachView && !isArchived
                      ? () => setConfirmDeletePhaseId(phase.phaseId)
                      : undefined}
                    confirmDelete={confirmDeletePhaseId === phase.phaseId}
                    onConfirmDelete={() => deletePhaseMutation.mutate(phase.phaseId)}
                    onCancelDelete={() => setConfirmDeletePhaseId(null)}
                  />
                )
              )}
              {showAddPhaseForm && (
                <PhaseAddForm
                  lastPhaseEndDate={lastPhaseEndDate}
                  onSave={(req) => createPhaseMutation.mutate(req)}
                  onCancel={() => { setShowAddPhaseForm(false); setPhaseAddError(null); }}
                  saving={createPhaseMutation.isPending}
                  error={phaseAddError}
                />
              )}
            </div>
          </div>

          {/* ── Shows section ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500">Shows</h4>
              {!isCoachView && !isArchived && !showAddForm && (
                <button
                  onClick={() => { setShowAddForm(true); setEditingPhaseId(null); }}
                  className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1 rounded font-medium">
                  + Add Show
                </button>
              )}
            </div>
            <div className="space-y-2">
              {sortedShows.length === 0 && !showAddForm && (
                <p className="text-sm text-gray-500 italic">
                  No shows yet. Add a show to this season.
                </p>
              )}
              {sortedShows.map(show =>
                editingShowId === show.showId ? (
                  <ShowEditPanel
                    key={show.showId}
                    show={show}
                    seasons={seasons}
                    onSave={(data) => onSaveShow(show.showId, data)}
                    onCancel={onCancelEditShow}
                    saving={showSaving}
                    error={showEditError}
                  />
                ) : (
                  <ShowRow
                    key={show.showId}
                    show={show}
                    onEdit={!isCoachView && !isArchived ? () => onEditShow(show.showId) : undefined}
                    onDelete={!isCoachView && !isArchived ? () => setConfirmDeleteShowId(show.showId) : undefined}
                    confirmDelete={confirmDeleteShowId === show.showId}
                    onConfirmDelete={() => deleteShowMutation.mutate(show.showId)}
                    onCancelDelete={() => setConfirmDeleteShowId(null)}
                  />
                )
              )}
              {showAddForm && (
                <ShowAddForm
                  onSave={(data) => createShowMutation.mutate(data)}
                  onCancel={() => { setShowAddForm(false); setShowAddError(null); }}
                  saving={createShowMutation.isPending}
                  error={showAddError}
                />
              )}
            </div>
          </div>

          <SeasonTimeline season={season} shows={sortedShows} phases={phases} />
        </div>
      )}
    </div>
  );
};

// ── Seasons page ──────────────────────────────────────────────────────────────

const Seasons: React.FC = () => {
  const { isCoachView, viewAsName, viewAs } = useCoachView();
  const queryClient = useQueryClient();

  // Season form state
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState<ContestSeason | null>(null);
  const [seasonFormError, setSeasonFormError] = useState<string | null>(null);

  // Season archive state
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Show edit state — one show open at a time across the whole page
  const [editingShowId, setEditingShowId] = useState<string | null>(null);
  const [showEditError, setShowEditError] = useState<string | null>(null);

  // ── data ──
  const { data: seasons = [], isLoading: seasonsLoading } = useQuery({
    queryKey: ['seasons', viewAs],
    queryFn: () => seasonService.getSeasons(viewAs ?? undefined),
  });

  const { data: allShows = [], isLoading: showsLoading } = useQuery({
    queryKey: ['shows', viewAs],
    queryFn: () => showService.getShows(viewAs ?? undefined),
  });

  const showsBySeason = (seasonId: string) => allShows.filter(s => s.seasonId === seasonId);
  const unassignedShows = allShows.filter(s => !s.seasonId);

  const activeSeasons    = seasons.filter(s => s.status === 'Active' || s.status === 'Planned');
  const completedSeasons = seasons.filter(s => s.status === 'Completed');
  const archivedSeasons  = seasons.filter(s => s.status === 'Archived');

  // ── season mutations ──
  const createSeasonMutation = useMutation({
    mutationFn: (req: CreateSeasonRequest) => seasonService.createSeason(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seasons'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      resetSeasonForm();
    },
    onError: () => setSeasonFormError('Failed to create season'),
  });

  const updateSeasonMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: UpdateSeasonRequest }) =>
      seasonService.updateSeason(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seasons'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      resetSeasonForm();
    },
    onError: () => setSeasonFormError('Failed to update season'),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => seasonService.archiveSeason(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seasons'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // ── show mutation ──
  const updateShowMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Show> }) =>
      showService.updateShow(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shows'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditingShowId(null);
      setShowEditError(null);
    },
    onError: () => setShowEditError('Failed to save show'),
  });

  // ── handlers ──
  const handleSeasonSave = (data: CreateSeasonRequest | UpdateSeasonRequest) => {
    setSeasonFormError(null);
    if (editingSeason) {
      updateSeasonMutation.mutate({ id: editingSeason.seasonId, req: data as UpdateSeasonRequest });
    } else {
      createSeasonMutation.mutate(data as CreateSeasonRequest);
    }
  };

  const handleEditSeason = (season: ContestSeason) => {
    setEditingSeason(season);
    setSeasonFormError(null);
    setShowSeasonForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetSeasonForm = () => {
    setEditingSeason(null);
    setSeasonFormError(null);
    setShowSeasonForm(false);
  };

  const handleEditShow = (id: string) => {
    setEditingShowId(id);
    setShowEditError(null);
  };

  const handleSaveShow = (id: string, data: Partial<Show>) => {
    setShowEditError(null);
    updateShowMutation.mutate({ id, data });
  };

  const seasonSaving = createSeasonMutation.isPending || updateSeasonMutation.isPending;
  const showSaving   = updateShowMutation.isPending;

  const sharedCardProps = {
    seasons,
    isCoachView,
    onArchive:            (id: string) => archiveMutation.mutate(id),
    confirmArchiveId,
    setConfirmArchiveId,
    editingShowId,
    onEditShow:           handleEditShow,
    onSaveShow:           handleSaveShow,
    onCancelEditShow:     () => { setEditingShowId(null); setShowEditError(null); },
    showSaving,
    showEditError,
  };

  if (seasonsLoading || showsLoading) {
    return <div className="text-center py-8 text-gray-400">Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">

      {/* Page header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gold-500 mb-2">Seasons</h1>
          <p className="text-gray-400">
            {isCoachView
              ? `Viewing ${viewAsName ?? 'athlete'}'s seasons — read only`
              : 'Manage your contest prep seasons and the shows within them'}
          </p>
        </div>
        {!isCoachView && !showSeasonForm && (
          <button
            onClick={() => setShowSeasonForm(true)}
            className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
          >
            + New Season
          </button>
        )}
      </div>

      {isCoachView && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-blue-900/30 border border-blue-600/40 text-blue-300 text-sm flex items-center gap-2">
          👁️ Coach view — you can see {viewAsName ?? 'this athlete'}'s seasons but cannot make changes.
        </div>
      )}

      {/* Season create / edit form */}
      {showSeasonForm && !isCoachView && (
        <div className="bg-dark-800 rounded-lg p-6 mb-6 border border-dark-600">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-2xl font-bold text-gold-500">
              {editingSeason ? 'Edit Season' : 'Create New Season'}
            </h2>
            <button onClick={resetSeasonForm} className="text-gray-400 hover:text-white">✕ Cancel</button>
          </div>
          <SeasonForm
            initial={editingSeason ?? undefined}
            onSave={handleSeasonSave}
            onCancel={resetSeasonForm}
            saving={seasonSaving}
            error={seasonFormError}
          />
        </div>
      )}

      {/* Active / Planned */}
      {activeSeasons.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">Active &amp; Planned</h2>
          <div className="space-y-3">
            {activeSeasons.map(season => (
              <SeasonCard key={season.seasonId} season={season}
                shows={showsBySeason(season.seasonId)}
                onEditSeason={handleEditSeason}
                {...sharedCardProps} />
            ))}
          </div>
        </section>
      )}

      {/* Completed */}
      {completedSeasons.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">Completed</h2>
          <div className="space-y-3">
            {completedSeasons.map(season => (
              <SeasonCard key={season.seasonId} season={season}
                shows={showsBySeason(season.seasonId)}
                onEditSeason={handleEditSeason}
                {...sharedCardProps} />
            ))}
          </div>
        </section>
      )}

      {/* Archived (hidden by default) */}
      {archivedSeasons.length > 0 && (
        <section className="mb-8">
          <button
            onClick={() => setShowArchived(v => !v)}
            className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-gray-600 hover:text-gray-400 mb-3 transition-colors"
          >
            <span>{showArchived ? '▼' : '▶'}</span>
            Archived ({archivedSeasons.length})
          </button>
          {showArchived && (
            <div className="space-y-3">
              {archivedSeasons.map(season => (
                <SeasonCard key={season.seasonId} season={season}
                  shows={showsBySeason(season.seasonId)}
                  onEditSeason={handleEditSeason}
                  {...sharedCardProps} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Empty state */}
      {seasons.length === 0 && (
        <div className="bg-dark-800 rounded-lg p-10 text-center border border-dark-600">
          <p className="text-gray-400 mb-2">No seasons yet</p>
          <p className="text-sm text-gray-500">
            Create a season to group your shows and track a full prep cycle in one place.
          </p>
        </div>
      )}

      {/* Unassigned shows */}
      {unassignedShows.length > 0 && (
        <section className="mt-4">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">Shows Without a Season</h2>
          <div className="bg-dark-800 rounded-lg p-4 border border-dark-600 space-y-2">
            {unassignedShows.map(show =>
              editingShowId === show.showId ? (
                <ShowEditPanel
                  key={show.showId}
                  show={show}
                  seasons={seasons}
                  onSave={(data) => handleSaveShow(show.showId, data)}
                  onCancel={() => { setEditingShowId(null); setShowEditError(null); }}
                  saving={showSaving}
                  error={showEditError}
                />
              ) : (
                <ShowRow
                  key={show.showId}
                  show={show}
                  onEdit={!isCoachView ? () => handleEditShow(show.showId) : undefined}
                />
              )
            )}
            {!unassignedShows.some(s => editingShowId === s.showId) && (
              <p className="text-xs text-gray-600 pt-1">
                Assign these shows to a season using the Edit button above, or from the{' '}
                <a href="/shows" className="text-gold-500 hover:underline">Shows page</a>.
              </p>
            )}
          </div>
        </section>
      )}

    </div>
  );
};

export default Seasons;
